import { AuthStorage } from "utils/auth-storage";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useParams } from "react-router-dom";
import {
  Breadcrumb,
  Button,
  Dropdown,
  Empty,
  Select,
  Table,
  Tooltip,
} from "antd";
import { DownOutlined, LeftOutlined } from "@ant-design/icons";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import ReactECharts from "echarts-for-react";
import moment from "moment";
import LoadingComponent from "components/loading-component";
import ExportReports from "components/export-reports";
import ExportReportsCSV from "components/export-reports-csv";
import SidebarDistribution from "components/sidebar-distribution/sidebar-distribution";
import Text from "components/text";
import { WarehouseServices } from "apis/Services/WarehouseService";
import {
  INVENTORY_ITEMS_HISTORY_REPORT,
  INVENTORY_ITEMS_NEVER_MOVED_REPORT,
  INVENTORY_ITEMS_NO_MOVEMENT_30_DAYS_REPORT,
  INVENTORY_ITEMS_STOCK_VS_DRAFT_REPORT,
  WAREHOUSE_REPORTS_BY_SLUG,
} from "pages/warehouse-reports/report-configs";
import styles from "pages/warehouse-reports/warehouse-reports.module.css";

const EXPORT_PROJECT_NAME = "Warehouse";
const ROW_CLICK_TOOLTIP =
  "Click this row to update the chart with this item's activity history.";
const BLANK_FILTER_VALUE = "__blank__";
const EMPTY_CHART_MODEL = {
  periods: [],
  groups: [],
};
const SORT_OPTIONS = [
  { label: "Descending", value: "Descending" },
  { label: "Ascending", value: "Ascending" },
];
const CHART_COLORS = [
  "#2563EB",
  "#F97316",
  "#14B8A6",
  "#E11D48",
  "#7C3AED",
  "#84CC16",
  "#DC2626",
  "#0EA5E9",
  "#D97706",
  "#059669",
];

function firstFilledValue(...values) {
  for (const value of values) {
    if (value === null || value === undefined) {
      continue;
    }

    if (typeof value === "string" && value.trim() === "") {
      continue;
    }

    return value;
  }

  return "";
}

function normalizeTextValue(value) {
  return String(firstFilledValue(value, "")).trim();
}

function compareTextValues(leftValue, rightValue) {
  return normalizeTextValue(leftValue).localeCompare(
    normalizeTextValue(rightValue),
    undefined,
    {
      numeric: true,
      sensitivity: "base",
    },
  );
}

function compareNumberValues(leftValue, rightValue) {
  const leftNumber = Number(firstFilledValue(leftValue, 0));
  const rightNumber = Number(firstFilledValue(rightValue, 0));

  return (
    (Number.isNaN(leftNumber) ? 0 : leftNumber) -
    (Number.isNaN(rightNumber) ? 0 : rightNumber)
  );
}

function parseMovementDate(value) {
  const normalizedValue = normalizeTextValue(value);

  if (!normalizedValue) {
    return null;
  }

  const strictDate = moment(
    normalizedValue,
    [
      "MM-DD-YYYY",
      "MM/DD/YYYY",
      "YYYY-MM-DD",
      "YYYY/MM/DD",
      "MM-DD-YYYY HH:mm:ss",
      "MM/DD/YYYY HH:mm:ss",
      "YYYY-MM-DD HH:mm:ss",
      "YYYY/MM/DD HH:mm:ss",
      moment.ISO_8601,
    ],
    true,
  );

  if (strictDate.isValid()) {
    return strictDate;
  }

  const fallbackDate = moment(normalizedValue);

  return fallbackDate.isValid() ? fallbackDate : null;
}

function compareDateValues(leftValue, rightValue) {
  const leftDate = parseMovementDate(leftValue);
  const rightDate = parseMovementDate(rightValue);

  if (!leftDate && !rightDate) {
    return 0;
  }

  if (!leftDate) {
    return 1;
  }

  if (!rightDate) {
    return -1;
  }

  return leftDate.valueOf() - rightDate.valueOf();
}

function formatCurrency(value) {
  const numericValue = Number(value || 0);

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(numericValue);
}

function formatMovementType(value) {
  return normalizeTextValue(value) || "Unspecified";
}

function buildWeekRangeLabel(weekStart, weekEnd) {
  if (!weekStart || !weekEnd) {
    return "";
  }

  return `${weekStart.format("MMM D")} - ${weekEnd.format("MMM D, YYYY")}`;
}

function getIsoWeekRange(date) {
  if (!date) {
    return {
      weekStart: null,
      weekEnd: null,
    };
  }

  const weekStart = date.clone().startOf("isoWeek").startOf("day");
  const weekEnd = weekStart.clone().endOf("isoWeek");

  return {
    weekStart,
    weekEnd,
  };
}

function createColumn({
  title,
  key,
  dataIndex = key,
  width = 180,
  fixed,
  filterValue,
  sorter,
  render,
}) {
  return {
    title,
    dataIndex,
    key,
    width,
    fixed,
    filterValue: filterValue || ((record) => record?.[dataIndex]),
    sorter,
    ...(render ? { render } : {}),
  };
}

function createTextColumn(options) {
  return createColumn({
    ...options,
    sorter: (left, right) =>
      compareTextValues(
        left?.[options.dataIndex || options.key],
        right?.[options.dataIndex || options.key],
      ),
  });
}

function createNumberColumn(options) {
  return createColumn({
    ...options,
    sorter: (left, right) =>
      compareNumberValues(
        left?.[options.dataIndex || options.key],
        right?.[options.dataIndex || options.key],
      ),
  });
}

function createDateColumn(options) {
  return createColumn({
    ...options,
    sorter: (left, right) =>
      compareDateValues(
        left?.[options.dataIndex || options.key],
        right?.[options.dataIndex || options.key],
      ),
  });
}

function createCurrencyColumn(options) {
  const dataIndex = options.dataIndex || options.key;

  return createColumn({
    ...options,
    dataIndex,
    filterValue:
      options.filterValue || ((record) => formatCurrency(record?.[dataIndex])),
    sorter: (left, right) =>
      compareNumberValues(left?.[dataIndex], right?.[dataIndex]),
    render: options.render || ((value) => formatCurrency(value)),
  });
}

const HISTORY_COLUMNS = [
  createTextColumn({
    title: "Material Name",
    key: "materialName",
    width: 220,
    fixed: "left",
  }),
  createTextColumn({
    title: "SKU",
    key: "sku",
    width: 160,
  }),
  createTextColumn({
    title: "Category",
    key: "category",
    width: 160,
  }),
  createTextColumn({
    title: "Subcategory",
    key: "subcategory",
    width: 180,
  }),
  createNumberColumn({
    title: "Current Stock",
    key: "currentStock",
    width: 140,
  }),
  createTextColumn({
    title: "Storage Location",
    key: "storageLocation",
    width: 180,
  }),
  createNumberColumn({
    title: "Activity Count",
    key: "movementCount",
    width: 150,
  }),
  createTextColumn({
    title: "Activity Type",
    key: "movementType",
    width: 150,
  }),
  createNumberColumn({
    title: "Quantity",
    key: "quantity",
    width: 130,
  }),
  createCurrencyColumn({
    title: "Unit Price",
    key: "unitPrice",
    width: 140,
  }),
  createDateColumn({
    title: "Activity Date",
    key: "movementDate",
    width: 180,
  }),
  createTextColumn({
    title: "From Location",
    key: "fromLocation",
    width: 180,
  }),
  createTextColumn({
    title: "To Location",
    key: "toLocation",
    width: 180,
  }),
  createTextColumn({
    title: "To Sub Location",
    key: "toSubLocation",
    width: 180,
  }),
];

const NO_MOVEMENT_30_DAYS_COLUMNS = [
  createTextColumn({
    title: "Material Name",
    key: "materialName",
    width: 220,
    fixed: "left",
  }),
  createTextColumn({
    title: "SKU",
    key: "sku",
    width: 160,
  }),
  createTextColumn({
    title: "Category",
    key: "category",
    width: 160,
  }),
  createTextColumn({
    title: "Subcategory",
    key: "subcategory",
    width: 180,
  }),
  createNumberColumn({
    title: "Current Stock",
    key: "currentStock",
    width: 140,
  }),
  createTextColumn({
    title: "Storage Location",
    key: "storageLocation",
    width: 180,
  }),
  createNumberColumn({
    title: "Activity Count",
    key: "movementCount",
    width: 150,
  }),
  createDateColumn({
    title: "Last Activity Date",
    key: "lastMovementDate",
    width: 190,
  }),
  createTextColumn({
    title: "Last Activity Type",
    key: "lastMovementType",
    width: 180,
  }),
  createNumberColumn({
    title: "Last Activity Quantity",
    key: "lastMovementQuantity",
    width: 190,
  }),
  createNumberColumn({
    title: "Days Since Last Activity",
    key: "daysSinceLastMoved",
    width: 180,
  }),
];

const NEVER_MOVED_COLUMNS = [
  createTextColumn({
    title: "Material Name",
    key: "materialName",
    width: 220,
    fixed: "left",
  }),
  createTextColumn({
    title: "SKU",
    key: "sku",
    width: 160,
  }),
  createTextColumn({
    title: "Category",
    key: "category",
    width: 160,
  }),
  createTextColumn({
    title: "Subcategory",
    key: "subcategory",
    width: 180,
  }),
  createNumberColumn({
    title: "Current Stock",
    key: "currentStock",
    width: 140,
  }),
  createTextColumn({
    title: "Storage Location",
    key: "storageLocation",
    width: 180,
  }),
];

const STOCK_VS_DRAFT_COLUMNS = [
  createTextColumn({
    title: "Material Name",
    key: "materialName",
    width: 220,
    fixed: "left",
  }),
  createTextColumn({
    title: "SKU",
    key: "sku",
    width: 160,
  }),
  createTextColumn({
    title: "Category",
    key: "category",
    width: 160,
  }),
  createTextColumn({
    title: "Subcategory",
    key: "subcategory",
    width: 180,
  }),
  createTextColumn({
    title: "Storage Location",
    key: "storageLocation",
    width: 180,
  }),
  createNumberColumn({
    title: "Current Stock",
    key: "currentStock",
    width: 140,
  }),
  createNumberColumn({
    title: "Allocated Stock",
    key: "stockNeededForDrafts",
    width: 190,
  }),
  createNumberColumn({
    title: "Free Stock",
    key: "expectedRemainingStock",
    width: 210,
  }),
];

const REPORT_COLUMNS_BY_SLUG = {
  [INVENTORY_ITEMS_HISTORY_REPORT]: HISTORY_COLUMNS,
  [INVENTORY_ITEMS_NO_MOVEMENT_30_DAYS_REPORT]: NO_MOVEMENT_30_DAYS_COLUMNS,
  [INVENTORY_ITEMS_NEVER_MOVED_REPORT]: NEVER_MOVED_COLUMNS,
  [INVENTORY_ITEMS_STOCK_VS_DRAFT_REPORT]: STOCK_VS_DRAFT_COLUMNS,
};

function normalizeWarehouseReportRows(dataSource) {
  return dataSource.map((entry, index) => {
    const movementTypeLabel = formatMovementType(
      entry?.movementType || entry?.lastMovementType,
    );
    const movementTypeKey = movementTypeLabel.toLowerCase();
    const movementDateValue = entry?.movementDate || entry?.lastMovementDate;
    const movementDate = parseMovementDate(movementDateValue);
    const { weekStart, weekEnd } = getIsoWeekRange(movementDate);
    const weekKey = weekStart?.format("YYYY-MM-DD") || "";
    const weekLabel = weekStart?.format("MMM D") || "";
    const weekRangeLabel = buildWeekRangeLabel(weekStart, weekEnd);

    return {
      key: `${entry?.itemID || entry?.materialName || "inventory-item"}-${
        movementDateValue || "movement"
      }-${index}`,
      ...entry,
      __movementTypeKey: movementTypeKey,
      __movementTypeLabel: movementTypeLabel,
      __weekKey: weekKey,
      __weekLabel: weekLabel,
      __weekRangeLabel: weekRangeLabel,
    };
  });
}

function buildWeeklyChartModel(rows) {
  const periodMap = new Map();
  const groupMap = new Map();

  rows.forEach((row) => {
    if (!row.__weekKey) {
      return;
    }

    if (!groupMap.has(row.__movementTypeKey)) {
      groupMap.set(row.__movementTypeKey, {
        key: row.__movementTypeKey,
        label: row.__movementTypeLabel,
        color: CHART_COLORS[groupMap.size % CHART_COLORS.length],
      });
    }

    if (!periodMap.has(row.__weekKey)) {
      const weekSortDate = parseMovementDate(row.__weekKey);

      periodMap.set(row.__weekKey, {
        key: row.__weekKey,
        label: row.__weekLabel,
        fullLabel: row.__weekRangeLabel,
        sortValue: weekSortDate ? weekSortDate.valueOf() : 0,
        totalCount: 0,
        groupCounts: {},
      });
    }

    const period = periodMap.get(row.__weekKey);
    period.totalCount += 1;
    period.groupCounts[row.__movementTypeKey] =
      (period.groupCounts[row.__movementTypeKey] || 0) + 1;
  });

  const periods = Array.from(periodMap.values()).sort(
    (left, right) => left.sortValue - right.sortValue,
  );
  const groups = Array.from(groupMap.values()).sort((left, right) =>
    compareTextValues(left.label, right.label),
  );

  return {
    periods,
    groups,
  };
}

function buildDetailedChartModel(periodEntries) {
  const groupMap = new Map();
  const periodMap = new Map();

  (Array.isArray(periodEntries) ? periodEntries : []).forEach(
    (entry, index) => {
      const startDate = parseMovementDate(entry?.startDate);
      const endDate = parseMovementDate(entry?.endDate);
      const { weekStart, weekEnd } = getIsoWeekRange(startDate || endDate);
      const periodKey =
        weekStart?.format("YYYY-MM-DD") ||
        `${normalizeTextValue(entry?.startDate)}-${normalizeTextValue(
          entry?.endDate,
        )}-${index}`;

      if (!periodMap.has(periodKey)) {
        periodMap.set(periodKey, {
          key: periodKey,
          label: weekStart
            ? weekStart.format("MMM D")
            : normalizeTextValue(entry?.startDate),
          fullLabel:
            buildWeekRangeLabel(weekStart, weekEnd) ||
            normalizeTextValue(entry?.startDate),
          sortValue: weekStart ? weekStart.valueOf() : index,
          totalCount: 0,
          groupCounts: {},
        });
      }

      const period = periodMap.get(periodKey);
      let entryTotal =
        Number(entry?.totalMoved) ||
        Object.values(entry?.byMovementType || {}).reduce(
          (accumulator, value) => accumulator + Number(value || 0),
          0,
        );

      period.totalCount += entryTotal;

      Object.entries(entry?.byMovementType || {}).forEach(([key, value]) => {
        const movementTypeLabel = formatMovementType(key);
        const movementTypeKey = movementTypeLabel.toLowerCase();

        if (!groupMap.has(movementTypeKey)) {
          groupMap.set(movementTypeKey, {
            key: movementTypeKey,
            label: movementTypeLabel,
            color: CHART_COLORS[groupMap.size % CHART_COLORS.length],
          });
        }

        period.groupCounts[movementTypeKey] =
          (period.groupCounts[movementTypeKey] || 0) + Number(value || 0);
      });
    },
  );

  const periods = Array.from(periodMap.values()).sort(
    (left, right) => left.sortValue - right.sortValue,
  );

  const groups = Array.from(groupMap.values()).sort((left, right) =>
    compareTextValues(left.label, right.label),
  );

  return {
    periods,
    groups,
  };
}

function buildChartOption(periods, groups) {
  return {
    color: groups.map((group) => group.color),
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "shadow",
      },
      backgroundColor: "#ffffff",
      borderColor: "#d8e3f0",
      borderWidth: 1,
      textStyle: {
        color: "#1f2937",
      },
      formatter: (params) => {
        if (!params?.length) {
          return "";
        }

        const period = periods[params[0].dataIndex];
        const rows = [
          `<div style="font-weight:600;margin-bottom:6px;">${
            period?.fullLabel || period?.label || ""
          }</div>`,
        ];

        params
          .filter((item) => Number(item?.value) > 0)
          .forEach((item) => {
            rows.push(
              `<div style="margin:3px 0;color:${item.color};">${item.seriesName}: ${item.value}</div>`,
            );
          });

        rows.push(
          `<div style="margin-top:8px;font-weight:600;color:#111827;">Total: ${
            period?.totalCount || 0
          }</div>`,
        );

        return rows.join("");
      },
    },
    legend: {
      type: "scroll",
      selectedMode: false,
      bottom: 0,
      itemWidth: 12,
      itemHeight: 12,
      textStyle: {
        fontSize: 12,
        color: "#516173",
      },
    },
    grid: {
      left: 36,
      right: 24,
      top: 44,
      bottom: 76,
      containLabel: true,
    },
    xAxis: {
      type: "category",
      data: periods.map((period) => period.label),
      axisTick: {
        show: false,
      },
      axisLine: {
        lineStyle: {
          color: "#dce6f2",
        },
      },
      axisLabel: {
        color: "#62758c",
      },
    },
    yAxis: {
      type: "value",
      minInterval: 1,
      splitLine: {
        lineStyle: {
          type: "dashed",
          color: "#e7eef6",
        },
      },
      axisLine: {
        show: false,
      },
      axisLabel: {
        color: "#62758c",
      },
    },
    series: groups.map((group) => ({
      id: group.key,
      name: group.label,
      type: "bar",
      stack: "inventory-movements",
      emphasis: {
        focus: "series",
      },
      barWidth: "100%",
      barMaxWidth: 100,
      data: periods.map((period) => period.groupCounts[group.key] || 0),
      itemStyle: {
        borderRadius: 0,
      },
    })),
  };
}

function getFilterOptionValue(value) {
  const normalizedValue = normalizeTextValue(value);

  return normalizedValue || BLANK_FILTER_VALUE;
}

function getFilterOptionLabel(value) {
  const normalizedValue = normalizeTextValue(value);

  return normalizedValue || "[Blank]";
}

function filterChartModelByWeekKeys(chartModel, selectedWeekKeys) {
  if (!Array.isArray(selectedWeekKeys) || selectedWeekKeys.length < 1) {
    return chartModel;
  }

  const selectedWeekKeySet = new Set(selectedWeekKeys);
  const periods = (chartModel?.periods || []).filter((period) =>
    selectedWeekKeySet.has(period.key),
  );
  const groups = (chartModel?.groups || []).filter((group) =>
    periods.some((period) => Number(period?.groupCounts?.[group.key] || 0) > 0),
  );

  return {
    periods,
    groups,
  };
}

function getWarehouseReportRows(response) {
  if (Array.isArray(response?.content)) {
    return response.content;
  }

  if (Array.isArray(response)) {
    return response;
  }

  return [];
}

function readWarehouseReport({
  company,
  code,
  loginEmail,
  reportSlug,
  sortBy,
}) {
  switch (reportSlug) {
    case INVENTORY_ITEMS_HISTORY_REPORT:
      return WarehouseServices.inventoryItemsHistory({
        company,
        code,
        loginEmail,
        sortBy,
      });
    case INVENTORY_ITEMS_NO_MOVEMENT_30_DAYS_REPORT:
      return WarehouseServices.inventoryItemsNoMovement30Days({
        company,
        code,
        loginEmail,
      });
    case INVENTORY_ITEMS_NEVER_MOVED_REPORT:
      return WarehouseServices.inventoryItemsNeverMoved({
        company,
        code,
        loginEmail,
      });
    case INVENTORY_ITEMS_STOCK_VS_DRAFT_REPORT:
      return WarehouseServices.inventoryItemsStockVsTransferDrafts({
        company,
        code,
        loginEmail,
      });
    default:
      return Promise.resolve({ content: [] });
  }
}

export default function WarehouseReportsDetails() {
  const { company_id, reportName } = useParams();
  const chartRef = useRef(null);
  const detailRequestIdRef = useRef(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isChartLoading, setIsChartLoading] = useState(false);
  const [accessLevel, setAccessLevel] = useState(null);
  const [sortBy, setSortBy] = useState("Descending");
  const [dataSource, setDataSource] = useState([]);
  const [columnFilters, setColumnFilters] = useState({});
  const [selectedChartWeekKeys, setSelectedChartWeekKeys] = useState([]);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [selectedDetailItem, setSelectedDetailItem] = useState(null);
  const [detailedHistoryPeriods, setDetailedHistoryPeriods] = useState([]);

  const activeReportConfig = WAREHOUSE_REPORTS_BY_SLUG[reportName] || null;
  const isSupportedReport = Boolean(activeReportConfig);
  const supportsChart = Boolean(activeReportConfig?.supportsChart);
  const supportsSortOrder = Boolean(activeReportConfig?.supportsSortOrder);
  const supportsRowDrilldown = reportName === INVENTORY_ITEMS_HISTORY_REPORT;
  const isDetailChartView =
    supportsRowDrilldown && Boolean(selectedDetailItem?.itemID);

  useEffect(() => {
    let isMounted = true;
    const code = AuthStorage.getItem("token");
    const email = AuthStorage.getItem("email");

    setIsLoading(true);
    WarehouseServices.inventoryPageAccessLevel({
      company: company_id,
      code,
      loginEmail: email,
    })
      .then((res) => {
        if (!isMounted) {
          return;
        }

        const nextAccessLevel = parseInt(res?.accessLevel, 10) || 0;
        setAccessLevel(nextAccessLevel);

        if (nextAccessLevel <= 0 || !isSupportedReport) {
          setDataSource([]);
          setSelectedSegment(null);
          setSelectedDetailItem(null);
          setDetailedHistoryPeriods([]);
          setColumnFilters({});
          setIsChartLoading(false);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        console.log(err);

        if (isMounted) {
          setAccessLevel(0);
          setDataSource([]);
          setSelectedSegment(null);
          setSelectedDetailItem(null);
          setDetailedHistoryPeriods([]);
          setColumnFilters({});
          setIsChartLoading(false);
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [company_id, isSupportedReport]);

  useEffect(() => {
    if (!isSupportedReport || accessLevel === null || accessLevel <= 0) {
      return;
    }

    let isMounted = true;
    const code = AuthStorage.getItem("token");
    const email = AuthStorage.getItem("email");

    setIsLoading(true);
    setColumnFilters({});
    setSelectedChartWeekKeys([]);
    setSelectedSegment(null);
    setSelectedDetailItem(null);
    setDetailedHistoryPeriods([]);
    setIsChartLoading(false);
    detailRequestIdRef.current += 1;

    readWarehouseReport({
      company: company_id,
      code,
      loginEmail: email,
      reportSlug: reportName,
      sortBy,
    })
      .then((res) => {
        if (!isMounted) {
          return;
        }

        setDataSource(getWarehouseReportRows(res));
      })
      .catch((err) => {
        console.log(err);

        if (isMounted) {
          setDataSource([]);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [accessLevel, company_id, isSupportedReport, reportName, sortBy]);

  const baseColumns = useMemo(
    () => REPORT_COLUMNS_BY_SLUG[reportName] || [],
    [reportName],
  );

  const normalizedDataSource = useMemo(
    () => normalizeWarehouseReportRows(dataSource),
    [dataSource],
  );

  const baseOverviewChartModel = useMemo(
    () =>
      supportsChart
        ? buildWeeklyChartModel(normalizedDataSource)
        : EMPTY_CHART_MODEL,
    [normalizedDataSource, supportsChart],
  );

  const baseDetailedChartModel = useMemo(
    () =>
      supportsChart
        ? buildDetailedChartModel(detailedHistoryPeriods)
        : EMPTY_CHART_MODEL,
    [detailedHistoryPeriods, supportsChart],
  );

  const baseActiveChartModel = supportsChart
    ? isDetailChartView
      ? baseDetailedChartModel
      : baseOverviewChartModel
    : EMPTY_CHART_MODEL;

  const chartWeekRangeOptions = useMemo(
    () =>
      (baseActiveChartModel?.periods || []).map((period) => ({
        label: period.fullLabel || period.label,
        value: period.key,
      })),
    [baseActiveChartModel],
  );

  useEffect(() => {
    if (!supportsChart) {
      setSelectedChartWeekKeys([]);
      return;
    }

    const availableWeekKeys = new Set(
      (baseActiveChartModel?.periods || []).map((period) => period.key),
    );

    setSelectedChartWeekKeys((previousValue) => {
      const nextValue = previousValue.filter((weekKey) =>
        availableWeekKeys.has(weekKey),
      );

      return nextValue.length === previousValue.length
        ? previousValue
        : nextValue;
    });
  }, [baseActiveChartModel, supportsChart]);

  const activeChartModel = useMemo(
    () =>
      filterChartModelByWeekKeys(baseActiveChartModel, selectedChartWeekKeys),
    [baseActiveChartModel, selectedChartWeekKeys],
  );

  const overviewRowsForWeekFilter = useMemo(() => {
    if (
      !supportsChart ||
      isDetailChartView ||
      !Array.isArray(selectedChartWeekKeys) ||
      selectedChartWeekKeys.length < 1
    ) {
      return normalizedDataSource;
    }

    const selectedWeekKeySet = new Set(selectedChartWeekKeys);

    return normalizedDataSource.filter((row) =>
      selectedWeekKeySet.has(row.__weekKey),
    );
  }, [
    isDetailChartView,
    normalizedDataSource,
    selectedChartWeekKeys,
    supportsChart,
  ]);

  const tableRows = useMemo(() => {
    if (!supportsChart || !selectedSegment) {
      return overviewRowsForWeekFilter;
    }

    return overviewRowsForWeekFilter.filter(
      (row) =>
        row.__weekKey === selectedSegment.periodKey &&
        row.__movementTypeKey === selectedSegment.groupKey,
    );
  }, [overviewRowsForWeekFilter, selectedSegment, supportsChart]);

  const filterValueResolvers = useMemo(
    () =>
      baseColumns.reduce((accumulator, column) => {
        accumulator[column.key] =
          column.filterValue ||
          ((record) => record?.[column.dataIndex || column.key]);

        return accumulator;
      }, {}),
    [baseColumns],
  );

  const filteredTableRows = useMemo(
    () =>
      tableRows.filter((row) =>
        Object.entries(columnFilters).every(([columnKey, filterValues]) => {
          if (!Array.isArray(filterValues) || filterValues.length < 1) {
            return true;
          }

          const rowValue = getFilterOptionValue(
            filterValueResolvers[columnKey]?.(row),
          );

          return filterValues.some(
            (filterValue) => rowValue === normalizeTextValue(filterValue),
          );
        }),
      ),
    [columnFilters, filterValueResolvers, tableRows],
  );

  const filterOptionsByColumn = useMemo(
    () =>
      baseColumns.reduce((accumulator, column) => {
        const resolveFilterValue = filterValueResolvers[column.key];
        const optionMap = new Map();

        tableRows.forEach((row) => {
          const optionValue = getFilterOptionValue(resolveFilterValue(row));

          if (!optionMap.has(optionValue)) {
            optionMap.set(optionValue, {
              text: getFilterOptionLabel(resolveFilterValue(row)),
              value: optionValue,
            });
          }
        });

        accumulator[column.key] = Array.from(optionMap.values()).sort(
          (left, right) => compareTextValues(left.text, right.text),
        );

        return accumulator;
      }, {}),
    [baseColumns, filterValueResolvers, tableRows],
  );

  const columns = useMemo(() => {
    return baseColumns.map((column) => {
      const originalRender = column.render;
      const columnKey = column.key;
      const resolveFilterValue = filterValueResolvers[columnKey];

      return {
        ...column,
        filters: filterOptionsByColumn[columnKey] || [],
        filteredValue: columnFilters[columnKey] || null,
        onFilter: (value, record) =>
          getFilterOptionValue(resolveFilterValue(record)) ===
          normalizeTextValue(value),
        render: (value, record, index) => {
          const content = originalRender
            ? originalRender(value, record, index)
            : firstFilledValue(value, "");

          if (!supportsRowDrilldown || !record?.itemID) {
            return content;
          }

          return (
            <Tooltip title={ROW_CLICK_TOOLTIP} placement="top">
              <div style={{ width: "100%", minHeight: 22 }}>{content}</div>
            </Tooltip>
          );
        },
      };
    });
  }, [
    baseColumns,
    columnFilters,
    filterOptionsByColumn,
    filterValueResolvers,
    supportsRowDrilldown,
  ]);

  useEffect(() => {
    if (!supportsChart || !selectedSegment || isDetailChartView) {
      return;
    }

    const segmentStillExists = overviewRowsForWeekFilter.some(
      (row) =>
        row.__weekKey === selectedSegment.periodKey &&
        row.__movementTypeKey === selectedSegment.groupKey,
    );

    if (!segmentStillExists) {
      setSelectedSegment(null);
    }
  }, [
    isDetailChartView,
    overviewRowsForWeekFilter,
    selectedSegment,
    supportsChart,
  ]);

  const chartOption = useMemo(
    () => buildChartOption(activeChartModel.periods, activeChartModel.groups),
    [activeChartModel.groups, activeChartModel.periods],
  );

  const handleChartClick = useCallback(
    (params) => {
      if (
        !supportsChart ||
        isDetailChartView ||
        !params?.seriesId ||
        params?.dataIndex === undefined
      ) {
        return;
      }

      const nextPeriod = activeChartModel.periods[params.dataIndex];
      const nextGroup = activeChartModel.groups.find(
        (group) => group.key === params.seriesId,
      );

      if (!nextPeriod || !nextGroup) {
        return;
      }

      setSelectedSegment((previousValue) => {
        if (
          previousValue?.periodKey === nextPeriod.key &&
          previousValue?.groupKey === nextGroup.key
        ) {
          return null;
        }

        return {
          periodKey: nextPeriod.key,
          periodLabel: nextPeriod.fullLabel,
          groupKey: nextGroup.key,
          groupLabel: nextGroup.label,
        };
      });
    },
    [
      activeChartModel.groups,
      activeChartModel.periods,
      isDetailChartView,
      supportsChart,
    ],
  );

  const handleRowClick = useCallback(
    (record) => {
      if (!supportsRowDrilldown) {
        return;
      }

      const itemID = normalizeTextValue(record?.itemID);

      if (!itemID) {
        return;
      }

      if (selectedDetailItem?.itemID === itemID) {
        detailRequestIdRef.current += 1;
        setIsChartLoading(false);
        setSelectedDetailItem(null);
        setDetailedHistoryPeriods([]);
        return;
      }

      const requestId = detailRequestIdRef.current + 1;
      const code = AuthStorage.getItem("token");
      const email = AuthStorage.getItem("email");

      detailRequestIdRef.current = requestId;
      setIsChartLoading(true);
      setSelectedDetailItem({
        itemID,
        materialName: normalizeTextValue(record?.materialName),
        rowKey: record?.key,
      });

      WarehouseServices.inventoryItemsHistoryDetailed({
        company: company_id,
        code,
        loginEmail: email,
        itemID,
      })
        .then((res) => {
          if (detailRequestIdRef.current !== requestId) {
            return;
          }

          setDetailedHistoryPeriods(getWarehouseReportRows(res));
        })
        .catch((error) => {
          console.log(error);

          if (detailRequestIdRef.current !== requestId) {
            return;
          }

          setDetailedHistoryPeriods([]);
        })
        .finally(() => {
          if (detailRequestIdRef.current === requestId) {
            setIsChartLoading(false);
          }
        });
    },
    [company_id, selectedDetailItem?.itemID, supportsRowDrilldown],
  );

  const chartTitle = useMemo(() => {
    if (!supportsChart) {
      return "";
    }

    if (!isDetailChartView) {
      return "Weekly Item Activity";
    }

    return `${selectedDetailItem.itemID} Activity Over Time`;
  }, [isDetailChartView, selectedDetailItem, supportsChart]);

  const chartHint = useMemo(() => {
    if (!supportsChart) {
      return "";
    }

    if (!isDetailChartView) {
      return "View transfer and purchase weekly counts.";
    }

    const materialName = normalizeTextValue(selectedDetailItem?.materialName);

    if (materialName) {
      return `Showing moved quantities over time for ${materialName}. Click another row to load a different item, or return to the overview chart.`;
    }

    return "Showing moved quantities over time for the selected item. Click another row to load a different item, or return to the overview chart.";
  }, [isDetailChartView, selectedDetailItem, supportsChart]);

  const tableTitle = useMemo(() => {
    if (supportsChart && selectedSegment) {
      return `Filtered movements for ${selectedSegment.groupLabel} in ${selectedSegment.periodLabel}`;
    }

    return activeReportConfig?.tableTitle || "Report Results";
  }, [activeReportConfig?.tableTitle, selectedSegment, supportsChart]);

  const exportTableRows = useMemo(
    () =>
      filteredTableRows.map((row) => ({
        ...row,
        ...(reportName === INVENTORY_ITEMS_HISTORY_REPORT
          ? { unitPrice: formatCurrency(row?.unitPrice) }
          : {}),
      })),
    [filteredTableRows, reportName],
  );

  const tableScrollWidth = useMemo(() => {
    const columnsWidth = columns.reduce(
      (accumulator, column) => accumulator + (Number(column.width) || 180),
      0,
    );

    return Math.max(columnsWidth + 120, 1200);
  }, [columns]);

  const getExportVisuals = useCallback(() => {
    if (!supportsChart) {
      return [];
    }

    const echartsInstance = chartRef.current?.getEchartsInstance?.();

    if (
      isChartLoading ||
      !echartsInstance ||
      activeChartModel.periods.length < 1 ||
      activeChartModel.groups.length < 1
    ) {
      return [];
    }

    return [
      {
        title: chartTitle,
        subtitle: tableTitle,
        imageData: echartsInstance.getDataURL({
          type: "png",
          pixelRatio: 2,
          backgroundColor: "#ffffff",
        }),
        width: 282,
        height: 86,
        format: "PNG",
      },
    ];
  }, [
    activeChartModel.groups.length,
    activeChartModel.periods.length,
    chartTitle,
    isChartLoading,
    supportsChart,
    tableTitle,
  ]);

  const exportMenuItems = useMemo(
    () => [
      {
        key: "export-pdf",
        label: (
          <ExportReports
            data={exportTableRows}
            columns={columns}
            accessLevel={accessLevel}
            editedData={[]}
            isLoading={isLoading}
            reportTitle={activeReportConfig?.title || "Warehouse Report"}
            projectName={EXPORT_PROJECT_NAME}
            projAddress=""
            company_id={company_id}
            getExportVisuals={getExportVisuals}
          />
        ),
      },
      {
        key: "export-csv",
        label: (
          <ExportReportsCSV
            data={exportTableRows}
            columns={columns}
            accessLevel={accessLevel}
            editedData={[]}
            isLoading={isLoading}
            reportTitle={activeReportConfig?.title || "Warehouse Report"}
            projectName={EXPORT_PROJECT_NAME}
            projAddress=""
          />
        ),
      },
    ],
    [
      accessLevel,
      activeReportConfig?.title,
      columns,
      company_id,
      exportTableRows,
      getExportVisuals,
      isLoading,
    ],
  );

  const clearChartFocus = useCallback(() => {
    if (!supportsChart) {
      return;
    }

    if (isDetailChartView) {
      detailRequestIdRef.current += 1;
      setIsChartLoading(false);
      setSelectedDetailItem(null);
      setDetailedHistoryPeriods([]);
      return;
    }

    setSelectedSegment(null);
  }, [isDetailChartView, supportsChart]);

  return (
    <div className="bg-white p-4 rounded-3 d-flex flex-column justify-content-center">
      {isLoading && (
        <div className="row d-flex justify-content-center">
          <LoadingComponent />
        </div>
      )}

      {!isLoading && accessLevel === 0 && (
        <div className="row w-100 d-flex justify-content-center p-5">
          <div className="d-flex flex-column align-items-center text-center gap-3">
            <LockRoundedIcon style={{ fontSize: "5rem", color: "#999" }} />
            <h6 className="mt-2 mb-0 text-muted">
              You do not have the necessary permissions to access this page.
            </h6>
          </div>
        </div>
      )}

      {!isLoading && accessLevel > 0 && !isSupportedReport && (
        <div className="row w-100 d-flex justify-content-center p-5">
          <div className="d-flex flex-column align-items-center text-center gap-3">
            <h4 className="fw-bold mb-0">Report Not Found</h4>
            <Text>This warehouse canned report is not available.</Text>
            <Link
              to={`/main/company/${company_id}/warehouse-reports`}
              className={styles.linkStyle}
            >
              Back to Canned Reports
            </Link>
          </div>
        </div>
      )}

      {!isLoading && accessLevel > 0 && isSupportedReport && (
        <div style={{ width: "100%" }} className="align-self-center">
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
            <div>
              <Breadcrumb
                className="mb-3"
                items={[
                  {
                    title: (
                      <Link
                        to={`/main/company/${company_id}/warehouse-reports`}
                        className={styles.linkStyle}
                      >
                        <div className="d-flex justify-content-start align-items-center gap-1">
                          <LeftOutlined style={{ fontSize: "12px" }} />
                          Back to Canned Reports
                        </div>
                      </Link>
                    ),
                  },
                  {
                    title: <>{activeReportConfig?.title}</>,
                  },
                ]}
              />
              <h3 className="fw-bold p-0 m-0 mt-2">
                {activeReportConfig?.title}
              </h3>
              <p className="opacity-75 mt-2 mb-0">
                {activeReportConfig?.description}
              </p>
            </div>

            <div className="d-flex flex-wrap justify-content-end align-items-end gap-3">
              <div className="align-self-center d-flex gap-2">
                <SidebarDistribution
                  isLoading={isLoading}
                  reportName={activeReportConfig?.distributionReportName}
                  reportOwner="Koncurent"
                  isWarehouseReport={true}
                />
                <Dropdown menu={{ items: exportMenuItems }}>
                  <Button>
                    Export <DownOutlined />
                  </Button>
                </Dropdown>
              </div>
            </div>
          </div>

          <div className={styles.reportLayout}>
            {supportsChart && (
              <div className={styles.chartCard}>
                <div className={styles.chartHeader}>
                  <div>
                    <Text className={styles.chartTitle}>{chartTitle}</Text>
                    <p className={styles.chartHint}>{chartHint}</p>
                  </div>
                  <div className={styles.tableHeaderActions}>
                    {chartWeekRangeOptions.length > 0 && (
                      <div className={styles.inlineControl}>
                        <label>Weekly Ranges</label>
                        <Select
                          mode="multiple"
                          allowClear
                          maxTagCount="responsive"
                          placeholder="All weeks"
                          value={selectedChartWeekKeys}
                          options={chartWeekRangeOptions}
                          style={{ minWidth: 280, width: 320 }}
                          onChange={setSelectedChartWeekKeys}
                        />
                      </div>
                    )}
                    {(selectedSegment || isDetailChartView) && (
                      <Button size="small" onClick={clearChartFocus}>
                        {isDetailChartView ? "Back to Overview" : "Clear Focus"}
                      </Button>
                    )}
                  </div>
                </div>

                {isChartLoading ? (
                  <div className={styles.chartEmptyState}>
                    <LoadingComponent />
                  </div>
                ) : activeChartModel.periods.length > 0 &&
                  activeChartModel.groups.length > 0 ? (
                  <ReactECharts
                    ref={chartRef}
                    option={chartOption}
                    style={{ height: 440, width: "100%" }}
                    onEvents={
                      isDetailChartView
                        ? undefined
                        : { click: handleChartClick }
                    }
                  />
                ) : (
                  <div className={styles.chartEmptyState}>
                    <Empty
                      description={
                        isDetailChartView
                          ? "No activity history was returned for the selected item."
                          : "No weekly inventory movements found."
                      }
                    />
                  </div>
                )}
              </div>
            )}

            <div className={styles.tableCard}>
              <div className=" d-flex w-100  justify-content-end pb-3 ">
                {/* <Text className={styles.tableTitle}>{tableTitle}</Text> */}
                <div className={styles.tableHeaderActions}>
                  {supportsSortOrder && (
                    <div className={styles.inlineControl}>
                      <label>Sort Order</label>
                      <Select
                        value={sortBy}
                        options={SORT_OPTIONS}
                        style={{ width: 180 }}
                        onChange={setSortBy}
                      />
                    </div>
                  )}
                  <div>
                    <span className={styles.tableCount}>
                      {filteredTableRows.length} {activeReportConfig?.rowLabel}
                      {filteredTableRows.length === 1 ? "" : "s"}
                    </span>
                  </div>
                </div>
              </div>

              <Table
                dataSource={filteredTableRows}
                columns={columns}
                bordered
                pagination={{ pageSize: 100, showSizeChanger: false }}
                scroll={{
                  x: tableScrollWidth,
                  y: supportsChart ? "62vh" : "70vh",
                }}
                onChange={(_, filters) => {
                  setColumnFilters(filters);
                }}
                rowClassName={(record) => {
                  const classNames = [];

                  if (supportsRowDrilldown && record?.itemID) {
                    classNames.push(styles.clickableRow);
                  }

                  if (selectedDetailItem?.rowKey === record?.key) {
                    classNames.push(styles.selectedRow);
                  }

                  return classNames.join(" ");
                }}
                onRow={(record) =>
                  supportsRowDrilldown
                    ? {
                        onClick: () => handleRowClick(record),
                      }
                    : {}
                }
                locale={{
                  emptyText:
                    activeReportConfig?.emptyText ||
                    "No warehouse report data found.",
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
